import React from 'react';
import {Platform, useWindowDimensions, View, ViewStyle} from 'react-native';

import {CONTENT_COLUMN_GUTTER, CONTENT_COLUMN_MAX_WIDTH} from '../ui-controls/layout/ContentView';

import WebFooter from '.';

/**
 * Footer breakpoint from the Figma footer frames: footer/mob 161:8697 covers 320–1023, and
 * footer/tab 161:8571 at 1024 already uses the 4-column desktop layout. This is the footer's own
 * breakpoint, not `ScreenDimensionsConfig` (768 / 1200), which governs the rest of the page.
 */
export const FOOTER_DESKTOP_MIN_WIDTH = 1024;

/** Distance from the window edge to the ContentView content box on web; 0 on native. */
export const usePageGutter = () => {
  const {width} = useWindowDimensions();
  if (Platform.OS !== 'web') {
    return 0;
  }
  return (width - Math.min(width, CONTENT_COLUMN_MAX_WIDTH)) / 2 + CONTENT_COLUMN_GUTTER;
};

/**
 * Style for the scroll container that holds `SiteFooter`. Screens put that container inside
 * ContentView's padded, max-width column, and its `overflow-x: hidden` would clip the footer to the
 * column. This widens the container to the window and pads it back, so the page content stays where
 * it was and the footer can bleed to both edges (Figma homecontent 475:24518: footer/desk spans the
 * full 1440 frame, its content inset 64 as the header logo). `undefined` on native.
 */
export function usePageBleed(): ViewStyle | undefined {
  const gutter = usePageGutter();
  return gutter ? {marginHorizontal: -gutter, paddingHorizontal: gutter} : undefined;
}

/**
 * `parentStyle` for the ContentView of a screen that mounts `SiteFooter`. ContentView pads its
 * parent 10 top and bottom; on web the bottom 10 would show as a strip below the footer.
 * SHORTCUT: 0 is a reset with no space token; upgrade when ContentView takes a footer prop.
 */
export const FOOTER_PARENT_STYLE: ViewStyle | undefined =
  Platform.OS === 'web' ? {paddingBottom: 0} : undefined;

/**
 * The one place that decides whether and how the web footer renders (DIS-1841). Screens mount it
 * unconditionally at the end of their scroll content, in a scroll container styled with
 * `usePageBleed()`: it renders nothing on native, and on web it picks the footer layout from the
 * window width and bleeds the footer back out to the window edges.
 */
export function SiteFooter() {
  const {width} = useWindowDimensions();
  const gutter = usePageGutter();
  if (Platform.OS !== 'web') {
    return null;
  }
  return (
    <View style={{marginHorizontal: -gutter}} testID="site-footer-bleed">
      <WebFooter layout={width >= FOOTER_DESKTOP_MIN_WIDTH ? 'desktop' : 'mobile'} />
    </View>
  );
}
